import React, { useEffect, useState } from "react";

import { toast } from "react-toastify";
import {
	Bot,
	BotOff,
	Loader2,
	AlertTriangle,
	Play,
	Square,
	Activity,
} from "lucide-react";

import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../ui/select";
import ConfirmationModal from "../ConfirmationModal";

import api from "../../services/api";
import toastError from "../../errors/toastError";

// As durações que a tela oferece. "Personalizado" abre um campo de minutos, e
// "permanente" é a pausa sem prazo — que é como a pausa sempre funcionou.
const DURACOES = [
	{ valor: "30", rotulo: "30 minutos" },
	{ valor: "60", rotulo: "1 hora" },
	{ valor: "120", rotulo: "2 horas" },
	{ valor: "240", rotulo: "4 horas" },
	{ valor: "480", rotulo: "8 horas" },
	{ valor: "1440", rotulo: "24 horas" },
	{ valor: "permanente", rotulo: "Até eu reativar" },
	{ valor: "personalizado", rotulo: "Personalizado…" },
];

// Quanto falta, em português. O servidor não avisa quando o prazo vence — ele
// limpa a pausa na próxima mensagem que chegar —, então a conta é feita aqui.
const faltando = (ate) => {
	const restante = new Date(ate).getTime() - Date.now();

	if (restante <= 0) return "o prazo já venceu";

	const minutos = Math.round(restante / 60000);

	if (minutos < 60) return `faltam ${minutos} min`;

	const horas = Math.floor(minutos / 60);
	const resto = minutos % 60;

	return resto
		? `faltam ${horas}h${String(resto).padStart(2, "0")}`
		: `faltam ${horas}h`;
};

const TicketAutomationModal = ({ open, onClose, ticket, onSaved }) => {
	const [fluxos, setFluxos] = useState([]);
	const [agentes, setAgentes] = useState([]);
	const [flowId, setFlowId] = useState("");
	const [aiAgentId, setAiAgentId] = useState("");
	const [duracao, setDuracao] = useState("60");
	const [minutosPersonalizados, setMinutosPersonalizados] = useState(90);
	const [salvando, setSalvando] = useState(false);

	// Disparo manual: só o que o administrador liberou para o atendente.
	const [fluxosDisparaveis, setFluxosDisparaveis] = useState([]);
	const [fluxoParaRodar, setFluxoParaRodar] = useState("");
	const [rodando, setRodando] = useState(false);
	const [confirmarDisparo, setConfirmarDisparo] = useState(false);

	// O que está rodando (ou esperando resposta) nesta conversa agora, venha de
	// onde vier — disparo do atendente ou gatilho automático. É o que habilita
	// o botão de parar.
	const [execucaoAtiva, setExecucaoAtiva] = useState(null);
	const [parando, setParando] = useState(false);
	const [confirmarParada, setConfirmarParada] = useState(false);

	const pausado = Boolean(ticket?.botPaused);
	const ate = ticket?.botPausedUntil;

	const fluxoEscolhido = fluxosDisparaveis.find(
		(f) => String(f.id) === String(fluxoParaRodar)
	);

	// Relê o que pode ser disparado e o que está rodando. Separado do efeito
	// porque disparar e parar precisam atualizar a tela sem fechar o modal.
	const carregarEstadoDoDisparo = async () => {
		try {
			const { data } = await api.get(`/tickets/${ticket.id}/run-flow`);
			setFluxosDisparaveis(data?.flows || []);
			setExecucaoAtiva(data?.execucaoAtiva || null);
		} catch (err) {
			setFluxosDisparaveis([]);
			setExecucaoAtiva(null);
		}
	};

	useEffect(() => {
		if (!open) return;

		setFlowId(ticket?.flowId ? String(ticket.flowId) : "nenhum");
		setAiAgentId(ticket?.aiAgentId ? String(ticket.aiAgentId) : "nenhum");
		setFluxoParaRodar("");
		setConfirmarDisparo(false);
		setConfirmarParada(false);
		setExecucaoAtiva(null);

		(async () => {
			try {
				const [f, a] = await Promise.all([
					api.get("/flows"),
					api.get("/ai/agents"),
				]);
				setFluxos(f.data?.flows || f.data || []);
				setAgentes(a.data?.agents || a.data || []);
			} catch (err) {
				// Sem as listas o modal ainda serve para pausar; só os seletores
				// ficam vazios.
			}
		})();

		// Lista em chamada separada de propósito: quem só atende não tem
		// `flows:manage`, e a chamada acima falha para ele. Esta rota tem régua
		// própria e devolve apenas o que foi liberado no cadastro do fluxo —
		// mais o que já está rodando aqui, para o botão de parar não aparecer
		// depois da lista.
		carregarEstadoDoDisparo();
	}, [open, ticket]);

	const alternarPausa = async () => {
		setSalvando(true);
		try {
			// Reativar não manda duração; pausar manda, salvo no permanente.
			const minutos =
				duracao === "permanente"
					? null
					: duracao === "personalizado"
						? Number(minutosPersonalizados) || null
						: Number(duracao);

			const { data } = await api.put(`/tickets/${ticket.id}/bot`, {
				paused: !pausado,
				duracaoMinutos: pausado ? undefined : minutos,
			});

			toast.success(
				pausado ? "Robô liberado nesta conversa." : "Robô pausado."
			);
			if (onSaved) onSaved(data);
		} catch (err) {
			toastError(err);
		}
		setSalvando(false);
	};

	const salvarAutomacao = async () => {
		setSalvando(true);
		try {
			const { data } = await api.put(`/tickets/${ticket.id}/automation`, {
				flowId: flowId === "nenhum" ? null : Number(flowId),
				aiAgentId: aiAgentId === "nenhum" ? null : Number(aiAgentId),
			});

			toast.success("Automação desta conversa atualizada.");
			if (onSaved) onSaved(data);
			onClose();
		} catch (err) {
			toastError(err);
		}
		setSalvando(false);
	};

	// Disparo manual. É ordem explícita do atendente, então roda mesmo com o
	// robô pausado aqui — e a resposta do cliente ainda retoma o fluxo, porque
	// o backend marca esta execução como manual.
	const dispararFluxo = async () => {
		if (!fluxoEscolhido) return;

		setRodando(true);
		try {
			await api.post(`/tickets/${ticket.id}/run-flow`, {
				flowId: fluxoEscolhido.id,
			});

			toast.success(`Fluxo "${fluxoEscolhido.name}" iniciado nesta conversa.`);
			setFluxoParaRodar("");
			// O bloco "rodando agora" só aparece com o estado relido: sem isso o
			// botão de parar só existiria depois de fechar e abrir o modal.
			carregarEstadoDoDisparo();
		} catch (err) {
			// 409 é o caminho esperado: já existe execução ativa nesta conversa e
			// o clique foi recusado de propósito. Avisar importa — sem isso o
			// botão pareceria não ter feito nada.
			if (err?.response?.status === 409) {
				toast.warn(
					err.response.data?.message ||
						"Este fluxo já está rodando nesta conversa. Espere ele terminar."
				);
				carregarEstadoDoDisparo();
			} else {
				toastError(err);
			}
		}
		setRodando(false);
	};

	// Interrompe o que estiver rodando ou esperando resposta nesta conversa.
	// Vale para fluxo automático também: o pedido é sobre o que está
	// acontecendo aqui agora, não sobre quem apertou o botão antes.
	const pararFluxo = async () => {
		setParando(true);
		try {
			const { data } = await api.delete(`/tickets/${ticket.id}/run-flow`);

			if (data?.paradas > 0) {
				toast.success(
					data.fluxos?.length
						? `Fluxo "${data.fluxos.join(", ")}" interrompido nesta conversa.`
						: "Fluxo interrompido nesta conversa."
				);
			} else {
				toast.info("Não havia fluxo rodando nesta conversa.");
			}

			carregarEstadoDoDisparo();
		} catch (err) {
			toastError(err);
		}
		setParando(false);
	};

	return (
		<Dialog open={open} onOpenChange={(o) => !o && onClose()}>
			<DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-md">
				<DialogHeader>
					<DialogTitle>Automação desta conversa</DialogTitle>
				</DialogHeader>

				<div className="space-y-4">
					{/* Estado atual, em uma frase — é o que a pessoa vem conferir. */}
					<div className="rounded-lg border p-3 text-sm">
						<p className="flex items-center gap-2 font-medium">
							{pausado ? (
								<BotOff className="h-4 w-4 text-amber-500" />
							) : (
								<Bot className="h-4 w-4 text-emerald-500" />
							)}
							{pausado ? "Robô pausado aqui" : "Robô respondendo aqui"}
						</p>
						<p className="mt-1 text-xs text-muted-foreground">
							{pausado
								? ate
									? `Volta sozinho quando o prazo acabar — ${faltando(ate)}.`
									: "Sem prazo: só volta quando alguém reativar."
								: "Fluxo, bot de fila e IA podem responder nesta conversa."}
						</p>
					</div>

					{!pausado && (
						<div className="space-y-1.5">
							<Label>Pausar por quanto tempo</Label>
							<Select value={duracao} onValueChange={setDuracao}>
								<SelectTrigger>
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									{DURACOES.map((d) => (
										<SelectItem key={d.valor} value={d.valor}>
											{d.rotulo}
										</SelectItem>
									))}
								</SelectContent>
							</Select>

							{duracao === "personalizado" && (
								<Input
									type="number"
									min={1}
									aria-label="Minutos"
									value={minutosPersonalizados}
									onChange={(e) =>
										setMinutosPersonalizados(e.target.value)
									}
								/>
							)}

							<p className="text-xs text-muted-foreground">
								O prazo é conferido quando chega mensagem: passado o tempo, o
								robô volta sozinho.
							</p>
						</div>
					)}

					<Button
						type="button"
						variant={pausado ? "default" : "secondary"}
						className="w-full"
						disabled={salvando}
						onClick={alternarPausa}
					>
						{salvando && <Loader2 className="h-4 w-4 animate-spin" />}
						{pausado ? "Reativar o robô agora" : "Pausar o robô"}
					</Button>

					<div className="space-y-1.5 border-t pt-4">
						<Label>Fluxo desta conversa</Label>
						<Select value={flowId || undefined} onValueChange={setFlowId}>
							<SelectTrigger>
								<SelectValue placeholder="Usar o fluxo da conexão" />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="nenhum">Usar o fluxo da conexão</SelectItem>
								{fluxos.map((f) => (
									<SelectItem key={f.id} value={String(f.id)}>
										{f.name}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
						<p className="text-xs text-muted-foreground">
							Escolhido aqui, ele tem prioridade sobre o da conexão — só nesta
							conversa.
						</p>
					</div>

					<div className="space-y-1.5">
						<Label>Agente de IA desta conversa</Label>
						<Select
							value={aiAgentId || undefined}
							onValueChange={setAiAgentId}
						>
							<SelectTrigger>
								<SelectValue placeholder="Usar o agente da conexão" />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="nenhum">Usar o agente da conexão</SelectItem>
								{agentes.map((a) => (
									<SelectItem key={a.id} value={String(a.id)}>
										{a.name}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
						<p className="flex gap-1.5 rounded-md bg-muted p-2 text-xs leading-relaxed">
							<AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
							A IA continua desligada se a conexão estiver com ela desligada —
							escolher o agente aqui não liga o que está desligado lá.
						</p>
					</div>

					{/* O que está rodando aqui agora. Aparece mesmo sem nenhum fluxo
					    liberado para disparo manual: se um fluxo automático travou na
					    conversa, é justamente este o bloco que a pessoa veio procurar. */}
					{execucaoAtiva && (
						<div className="space-y-2 rounded-lg border border-amber-500/40 bg-amber-500/5 p-3">
							<p className="flex items-center gap-2 text-sm font-medium">
								<Activity className="h-4 w-4 text-amber-500" />
								{execucaoAtiva.status === "waiting"
									? "Fluxo esperando a resposta do cliente"
									: "Fluxo rodando nesta conversa"}
							</p>

							<p className="text-xs text-muted-foreground">
								<span className="font-medium text-foreground">
									{execucaoAtiva.flowName || "Fluxo"}
								</span>
								{" — "}
								{execucaoAtiva.manual
									? "disparado por um atendente."
									: "iniciado automaticamente."}
								{execucaoAtiva.status === "waiting" &&
									" Se o cliente responder, ele continua daqui."}
							</p>

							<Button
								type="button"
								variant="destructive"
								className="w-full"
								disabled={parando}
								onClick={() => setConfirmarParada(true)}
							>
								{parando ? (
									<Loader2 className="h-4 w-4 animate-spin" />
								) : (
									<Square className="h-4 w-4" />
								)}
								Parar o fluxo
							</Button>

							<p className="text-xs text-muted-foreground">
								Interrompe só aqui e só agora. O robô não fica pausado: se um
								gatilho disparar de novo, o fluxo roda outra vez — para impedir
								a reincidência, use "Pausar o robô".
							</p>
						</div>
					)}

					{/* Só aparece quando o administrador liberou algum fluxo para o
					    atendente — sem nada liberado, o bloco inteiro some e não
					    vira ruído para quem só veio pausar o robô. Enquanto há fluxo
					    rodando, some também: o backend recusa um segundo disparo, e
					    um seletor que só leva a erro é pior que a ausência dele. */}
					{!execucaoAtiva && fluxosDisparaveis.length > 0 && (
						<div className="space-y-1.5 border-t pt-4">
							<Label>Executar um fluxo agora</Label>

							<Select
								value={fluxoParaRodar || undefined}
								onValueChange={setFluxoParaRodar}
							>
								<SelectTrigger>
									<SelectValue placeholder="Escolha um fluxo" />
								</SelectTrigger>
								<SelectContent>
									{fluxosDisparaveis.map((f) => (
										<SelectItem key={f.id} value={String(f.id)}>
											{f.name}
										</SelectItem>
									))}
								</SelectContent>
							</Select>

							<Button
								type="button"
								variant="secondary"
								className="w-full"
								disabled={!fluxoParaRodar || rodando}
								onClick={() => setConfirmarDisparo(true)}
							>
								{rodando ? (
									<Loader2 className="h-4 w-4 animate-spin" />
								) : (
									<Play className="h-4 w-4" />
								)}
								Executar agora
							</Button>

							<p className="text-xs text-muted-foreground">
								O cliente recebe as mensagens deste fluxo de verdade, na hora.
								Roda mesmo com o robô pausado aqui.
							</p>
						</div>
					)}
				</div>

				<DialogFooter>
					<Button
						type="button"
						variant="outline"
						onClick={onClose}
						disabled={salvando}
					>
						Fechar
					</Button>
					<Button type="button" onClick={salvarAutomacao} disabled={salvando}>
						{salvando && <Loader2 className="h-4 w-4 animate-spin" />}
						Salvar
					</Button>
				</DialogFooter>
			</DialogContent>

			{/* Confirmação porque o disparo fala com o cliente de verdade e não
			    tem como ser desfeito — e o seletor já pode estar preenchido de
			    um clique anterior. */}
			<ConfirmationModal
				title="Executar fluxo nesta conversa?"
				open={confirmarDisparo}
				onClose={setConfirmarDisparo}
				onConfirm={dispararFluxo}
				confirmLabel="Executar agora"
				confirmVariant="default"
			>
				{fluxoEscolhido
					? `O fluxo "${fluxoEscolhido.name}" vai começar a falar com o cliente agora, no WhatsApp. As mensagens enviadas não têm como ser desfeitas.`
					: "O fluxo escolhido vai começar a falar com o cliente agora."}
			</ConfirmationModal>

			{/* Parar não é destrutivo — nada é apagado —, mas interrompe algo que
			    já está falando com o cliente e não tem como ser retomado de onde
			    parou. Por isso a confirmação. */}
			<ConfirmationModal
				title="Parar o fluxo nesta conversa?"
				open={confirmarParada}
				onClose={setConfirmarParada}
				onConfirm={pararFluxo}
				confirmLabel="Parar o fluxo"
			>
				{execucaoAtiva?.flowName
					? `O fluxo "${execucaoAtiva.flowName}" será interrompido agora. O que já foi enviado ao cliente continua enviado, e o fluxo não retoma de onde parou.`
					: "O fluxo em andamento nesta conversa será interrompido agora."}
			</ConfirmationModal>
		</Dialog>
	);
};

export default TicketAutomationModal;
